import React, { useState, useEffect } from "react";
import { useNavigate, Link } from 'react-router-dom';
import logoImage from '../images/Logo-Base-basic.png';
import Sidebar from '../components/SideBar';
import { ENDPOINT_PATH } from '../config';
import { getAdminOrders } from './orderViewHelper';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PeopleIcon from '@mui/icons-material/People';
import InventoryIcon from '@mui/icons-material/Inventory';
import PaletteIcon from '@mui/icons-material/Palette';
import BarChartIcon from '@mui/icons-material/BarChart';
import '../App.css';
import './adminDashboard.css';

const AdminNavbar = () => {
  return (
    <div className="navWrapper">
      <div className="AdminNav">
        <div>
          <a href="/" className="NavLink">
            <div className="AdminLogo d-flex justify-content-end">
              <img src={logoImage} alt='logo' className="NavLogo" />
            </div>
            <div className="AdminLogoText font-2 ps-2 fontLogo">mycustomframe</div>
          </a>
        </div>
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [userInfo, setUserInfo] = useState(null);
  const [pendingOrdersCount, setPendingOrdersCount] = useState(0);
  const [unfinishedRequestsCount, setUnfinishedRequestsCount] = useState(0);

  // Fetch statistics for quick stats cards
  const fetchStatistics = async () => {
    try {
      // Fetch pending orders (Not Started status)
      const pendingOrders = await getAdminOrders('Not Started', '', '');
      const pendingCount = Array.isArray(pendingOrders) ? pendingOrders.length : 
                          (pendingOrders?.data && Array.isArray(pendingOrders.data) ? pendingOrders.data.length : 0);
      setPendingOrdersCount(pendingCount);

      // Fetch unfinished dimension requests
      try {
        const unfinishedResponse = await fetch(`${ENDPOINT_PATH}/api/count-dimension-requests?filterType=unfinished`);
        const unfinishedData = await unfinishedResponse.json();
        setUnfinishedRequestsCount(unfinishedData.count || 0);
      } catch (error) {
        console.error("Error fetching unfinished requests count:", error);
      }
    } catch (error) {
      console.error("Error fetching statistics:", error);
    }
  };

  // Check if user is admin
  useEffect(() => {
    const checkAdminAccess = async () => {
      try {
        const user = JSON.parse(localStorage.getItem('user'));
        
        if (!user || !user.uid) {
          alert('Please login to access the Admin Portal');
          navigate('/');
          return;
        }
        
        // Verify user is an admin
        const response = await fetch(ENDPOINT_PATH + `/api/get-account-type?user=` + encodeURIComponent(user.uid));
        const accountType = await response.json();
        
        if (accountType.toLowerCase() !== 'admin') {
          alert('Access denied: Admin account required');
          navigate('/');
          return;
        }

        // Fetch user info for display
        try {
          const userInfoResponse = await fetch(ENDPOINT_PATH + `/api/get-user-info?user=` + encodeURIComponent(user.uid));
          const userData = await userInfoResponse.json();
          setUserInfo(userData);
        } catch (error) {
          console.error("Error fetching user info:", error);
        }

        // Fetch statistics
        fetchStatistics();
        
        setLoading(false);
      } catch (error) {
        console.error("Error checking admin access:", error);
        alert('Error verifying access. Please try again.');
        navigate('/');
      }
    };
    
    checkAdminAccess();
  }, [navigate]);

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column' 
      }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Verifying access...</p>
      </div>
    );
  }

  return (
    <div>
      <AdminNavbar />
      <hr className="admin-divider" />
      <div className="admin-dashboard-page">
        <Sidebar userType="admin" />
        <div className="admin-dashboard-content">
          {/* Welcome Banner */}
          <div className="welcome-banner">
            <h1 className="welcome-title">
              Welcome{userInfo ? `, ${userInfo.firstName || 'Admin'}` : ''}!
            </h1>
            <p className="welcome-subtitle">Your workspace for managing the platform</p>
            <div className="welcome-accent-line"></div>
          </div>

          {/* Quick Stats Row */}
          <div className="stats-row">
            <div className="stat-card">
              <div className="stat-content">
                <div className="stat-number">{pendingOrdersCount}</div>
                <div className="stat-label">Pending Orders</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-content">
                <div className="stat-number">{unfinishedRequestsCount}</div>
                <div className="stat-label">Active Requests</div>
              </div>
            </div>
          </div>

          {/* Full Access Card */}
          <div className="access-cards-row">
            <div className="access-card full-access">
              <div className="access-card-header">
                <CheckCircleIcon style={{ fontSize: '20px', color: 'white', marginRight: '8px' }} />
                <h5>Full Access</h5>
              </div>
              <div className="access-card-content">
                <div className="access-item">
                  <ShoppingCartIcon style={{ fontSize: '16px', color: '#ff4172', marginRight: '10px' }} />
                  <span><strong>Orders:</strong> View and update order status</span>
                </div>
                <div className="access-item">
                  <AssignmentIcon style={{ fontSize: '16px', color: '#ff4172', marginRight: '10px' }} />
                  <span><strong>Dimension Requests:</strong> Handle customer dimension requests</span>
                </div>
                <div className="access-item">
                  <PeopleIcon style={{ fontSize: '16px', color: '#ff4172', marginRight: '10px' }} />
                  <span><strong>Users:</strong> Manage user accounts and permissions</span>
                </div>
                <div className="access-item">
                  <InventoryIcon style={{ fontSize: '16px', color: '#ff4172', marginRight: '10px' }} />
                  <span><strong>Products:</strong> Manage products and pricing</span>
                </div>
                <div className="access-item">
                  <PaletteIcon style={{ fontSize: '16px', color: '#ff4172', marginRight: '10px' }} />
                  <span><strong>Artists & Artwork:</strong> Manage artists and artwork</span>
                </div>
                <div className="access-item">
                  <BarChartIcon style={{ fontSize: '16px', color: '#ff4172', marginRight: '10px' }} />
                  <span><strong>Metrics:</strong> View reports and analytics</span>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions Section */}
          <div className="quick-actions-section">
            <h3 className="quick-actions-title">Quick Actions</h3>
            <div className="quick-actions-row">
              <Link to="/orderHistory" className="quick-action-btn">
                <ShoppingCartIcon style={{ fontSize: '20px' }} />
                <span>View Orders</span>
              </Link>
              <Link to="/dimension-requests" className="quick-action-btn">
                <AssignmentIcon style={{ fontSize: '20px' }} />
                <span>Handle Requests</span>
              </Link>
              <Link to="/users" className="quick-action-btn">
                <PeopleIcon style={{ fontSize: '20px' }} />
                <span>Manage Users</span>
              </Link>
              <Link to="/productPage" className="quick-action-btn">
                <InventoryIcon style={{ fontSize: '20px' }} />
                <span>Manage Products</span>
              </Link>
              <Link to="/artistPage" className="quick-action-btn">
                <PaletteIcon style={{ fontSize: '20px' }} />
                <span>Manage Artists</span>
              </Link>
              <Link to="/metrics" className="quick-action-btn">
                <BarChartIcon style={{ fontSize: '20px' }} />
                <span>View Metrics</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;

